import React, { useState, useMemo } from "react";
import {
  Grid, Box, Typography, useMediaQuery, useTheme,
  TextField, InputAdornment, IconButton, Chip,
} from "@mui/material";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import CartItem from "./carrito/CartItem";
import CartTotal from "./carrito/CartTotal";
import CartDeleteDialog from "./carrito/CartDeleteDialog";
import SeccionDescuento from "./SeccionDescuentos";

// ─── Empty state ──────────────────────────────────────────────────────────────
const EmptyState = ({ icon, title, subtitle }) => (
  <Box sx={{ minHeight: 180, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, py: 4 }}>
    <Box sx={{ width: 56, height: 56, borderRadius: "var(--radius-full)", bgcolor: "var(--accent-soft)", display: "flex", alignItems: "center", justifyContent: "center", mb: 0.5 }}>
      {icon}
    </Box>
    <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "var(--text-primary)", fontFamily: "var(--font-heading)" }}>{title}</Typography>
    <Typography sx={{ fontSize: "0.78rem", color: "var(--text-muted)", fontFamily: "var(--font-body)", textAlign: "center" }}>{subtitle}</Typography>
  </Box>
);

// ─── SeccionCarrito ───────────────────────────────────────────────────────────
const SeccionCarrito = ({
  cartList, isCheckStock, changeAmount, deleteFromCart,
  formatToCurrency, onNext, descuento, descuentoAplicado,
  totalGeneral, totalFinal, montoMinimo,  
  isVendedor, descuentoMaximo, onApplyGeneralDiscount,onDiscountChange
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const totalItems = cartList.reduce((sum, i) => sum + i.amount, 0);
  const hasOverStockItem = isCheckStock &&
    cartList.some(i => typeof i.availableStock === "number" && i.amount > i.availableStock);

  const filteredCartList = useMemo(() => {
    if (!searchTerm.trim()) return cartList;
    const term = searchTerm.toLowerCase().trim();
    return cartList.filter(i =>
      i.title?.toLowerCase().includes(term) ||
      i.codigo?.toString().toLowerCase().includes(term) ||
      i.id?.toString().toLowerCase().includes(term)
    );
  }, [cartList, searchTerm]);

  const handleDeleteClick = (id) => { setSelectedId(id); setOpenConfirm(true); };
  const handleConfirmDelete = () => { deleteFromCart(selectedId); setSelectedId(null); setOpenConfirm(false); };
 
  return (
    <Grid item xs={12}>

      {/* ── Header ── */}
      <Box sx={{
        background: "linear-gradient(135deg, var(--accent) 0%, var(--accent-hover) 100%)",
        borderRadius: "var(--radius-lg) var(--radius-lg) 0 0",
        px: { xs: 2, md: 2.5 },
        py: { xs: 1.5, md: 1.75 },
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { md: "center" },
        justifyContent: { md: "space-between" },
        gap: { xs: 1.25, md: 0 },
      }}>

        {/* Título + contador */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box sx={{
            width: 36, height: 36,
            borderRadius: "var(--radius-sm)",
            bgcolor: "rgba(255,255,255,0.15)",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <ShoppingCartCheckoutIcon sx={{ color: "#fff", fontSize: 19 }} />
          </Box>
          <Box>
            <Typography sx={{
              color: "#fff", fontWeight: 700, fontSize: "1rem",
              fontFamily: "var(--font-heading)",
              lineHeight: 1.2, letterSpacing: "-0.01em",
            }}>
              Mi Carrito
            </Typography>
            {cartList.length > 0 && (
              <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.72rem", fontFamily: "var(--font-body)" }}>
                {totalItems} {totalItems === 1 ? "producto" : "productos"}
              </Typography>
            )}
          </Box>
          {!isMobile && cartList.length > 0 && (
            <Chip
              label={`${cartList.length} artículo${cartList.length !== 1 ? "s" : ""}`}
              size="small"
              sx={{
                ml: 1,
                bgcolor: "rgba(255,255,255,0.15)",
                color: "#fff", fontWeight: 600, fontSize: "0.7rem",
                fontFamily: "var(--font-body)",
                border: "1px solid rgba(255,255,255,0.25)",
              }}
            />
          )}
        </Box>

        {/* Buscador */}
        {cartList.length > 0 && (
          <TextField
            size="small"
            placeholder="Buscar producto..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            sx={{
              width: { xs: "100%", md: 260 },
              "& .MuiOutlinedInput-root": {
                bgcolor: "rgba(255,255,255,0.12)",
                borderRadius: "var(--radius-sm)",
                color: "#fff",
                fontSize: "0.82rem",
                fontFamily: "var(--font-body)",
                height: { xs: 36, md: 34 },
                transition: "background-color var(--transition-fast)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.18)" },
                "&.Mui-focused": { bgcolor: "rgba(255,255,255,0.22)" },
                "& fieldset": { borderColor: "rgba(255,255,255,0.2)" },
                "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.5)", borderWidth: "1px" },
                "& input::placeholder": { color: "rgba(255,255,255,0.45)", opacity: 1 },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.55)" }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchTerm("")} size="small" sx={{ p: 0.25 }}>
                    <ClearIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.6)" }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      </Box>

      {/* ── Cuerpo ── */}
      <Box sx={{
        bgcolor: "var(--bg-card)",
        borderRadius: "0 0 var(--radius-lg) var(--radius-lg)",
        border: "1.5px solid var(--border)",
        borderTop: "none",
        display: "flex",
        flexDirection: "column",
        height: { md: "60vh" },
      }}>

        {/* Lista */}
        <Box sx={{
          flex: 1,
          overflowY: { md: "auto" },
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { bgcolor: "var(--border-hover)", borderRadius: "var(--radius-full)" },
        }}>
          {cartList.length === 0 ? (
            <EmptyState
              icon={<ShoppingCartOutlinedIcon sx={{ fontSize: 26, color: "var(--accent)" }} />}
              title="Tu carrito está vacío"
              subtitle="Agregá productos para comenzar tu compra"
            />
          ) : filteredCartList.length === 0 ? (
            <EmptyState
              icon={<SearchIcon sx={{ fontSize: 24, color: "var(--accent)" }} />}
              title="Sin resultados"
              subtitle="Intentá con otro término de búsqueda"
            />
          ) : (
            <>
             {isVendedor && (
                    <SeccionDescuento
                      cartList={cartList}
                      descuentoMaximo={descuentoMaximo}
                      onApplyGeneral={onApplyGeneralDiscount}
                    />
                  )}
              {filteredCartList.map((item, index) => (
                <Box
                  key={item.id}
                  sx={{
                    animation: `slideIn 0.2s ease ${index * 0.04}s both`,
                    "@keyframes slideIn": {
                      from: { opacity: 0, transform: "translateY(5px)" },
                      to: { opacity: 1, transform: "translateY(0)" },
                    },
                    borderBottom: "1px solid var(--divider)",
                    "&:last-child": { borderBottom: "none" },
                  }}
                >
                 
                  <CartItem
                    item={item}
                    isMobile={isMobile}
                    isCheckStock={isCheckStock}
                    changeAmount={changeAmount}
                    handleDeleteClick={deleteFromCart}
                    formatToCurrency={formatToCurrency}
                    isVendedor={isVendedor}                    // ← AGREGAR
                    descuentoMaximo={descuentoMaximo}          // ← AGREGAR
                    onDiscountChange={onDiscountChange}        // ← AGREGAR
                    onApplyGeneralDiscount={onApplyGeneralDiscount}
                  />
                </Box>
              ))}

              {hasOverStockItem && !searchTerm && (
                <Box sx={{
                  mx: 1.5, my: 1.25, px: 2, py: 1.1,
                  borderRadius: "var(--radius-sm)",
                  bgcolor: "var(--danger-bg)",
                  border: "1px solid rgba(239,68,68,0.25)",
                  color: "var(--danger)",
                  fontSize: "0.78rem", fontWeight: 500,
                  fontFamily: "var(--font-body)",
                  display: "flex", alignItems: "center", gap: 1,
                }}>
                  <WarningAmberRoundedIcon sx={{ fontSize: 16, color: "var(--danger)", flexShrink: 0 }} />
                  Hay productos que superan el stock disponible
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{
          px: { xs: 1.5, md: 2 },
          py: { xs: 1.5, md: 1.75 },
          borderTop: "1.5px solid var(--divider)",
          bgcolor: "var(--bg-card)",
          borderRadius: "0 0 var(--radius-lg) var(--radius-lg)",
        }}>
          <CartTotal
            descuento={descuento}
            descuentoAplicado={descuentoAplicado}
            totalGeneral={totalGeneral}
            totalFinal={totalFinal}
            montoMinimo={montoMinimo}
            formatToCurrency={formatToCurrency}
            onNext={onNext}
            isDisabled={cartList.length === 0 || totalFinal < montoMinimo || hasOverStockItem}
          />
        </Box>
      </Box>

      <CartDeleteDialog
        open={openConfirm}
        onConfirm={handleConfirmDelete}
        onCancel={() => setOpenConfirm(false)}
      />
    </Grid>
  );
};

export default SeccionCarrito;